import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Clock, Footprints, ShieldCheck } from 'lucide-react';
import { Button } from '../components';
import { GespreksverslagEngineForm } from '../components/GespreksverslagEngineForm';
import { duurLabel, getAdhocWerkgevers } from '../services/adhocUrenApi';
import { getVerslagContext, type VerslagResultaat } from '../services/gespreksverslagApi';

/**
 * Ad-hoc werkvloermelding — route 3 (migratie 173/176). Een gesprek zonder
 * afspraak vooraf (zichtbaarheidsronde) krijgt hier meteen een volledig
 * gespreksverslag. De medewerker bevestigt via een SMS-link (72 uur); tot dan
 * staan de uren niet op de verzamelfactuur en telt het gesprek niet mee in het
 * jaarverslag of de B2B-update (anti-fraudegrendel).
 */
const vandaag = () => new Date().toLocaleDateString('sv-SE');
const dagenTerug = (n: number) => new Date(Date.now() - n * 86_400_000).toLocaleDateString('sv-SE');
const DUUR_OPTIES = Array.from({ length: 32 }, (_, i) => (i + 1) * 15);

const leegGesprek = { employer_id: '', datum: vandaag(), tijd_minuten: 30, employee_phone: '', toelichting: '' };

const ProfessionalAdhocMeldingPage: React.FC = () => {
  const qc = useQueryClient();
  const [g, setG] = useState(leegGesprek);
  const [resultaat, setResultaat] = useState<VerslagResultaat | null>(null);
  const zet = <K extends keyof typeof leegGesprek>(k: K, v: (typeof leegGesprek)[K]) => setG((x) => ({ ...x, [k]: v }));

  const { data: werkgevers = [] } = useQuery({ queryKey: ['professional-adhoc-werkgevers'], queryFn: getAdhocWerkgevers });
  const { data: ctx, error } = useQuery({
    queryKey: ['verslag-context-adhoc', g.employer_id, g.datum],
    queryFn: () => getVerslagContext({ employer_id: g.employer_id, datum: g.datum }),
    enabled: !!g.employer_id && !!g.datum,
    retry: false,
  });

  const gesprekCompleet = !!g.employer_id && !!g.datum && g.employee_phone.trim().length >= 10;

  if (resultaat) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="w-6 h-6" aria-hidden />
            <h1 className="text-xl font-semibold">Melding geregistreerd</h1>
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
            <dt className="text-gray-500">Registratienummer</dt>
            <dd className="font-mono text-gray-900">{resultaat.registratienummer}</dd>
            {resultaat.case_code && (
              <>
                <dt className="text-gray-500">Case</dt>
                <dd className="font-mono text-gray-900">{resultaat.case_code}</dd>
              </>
            )}
            <dt className="text-gray-500">Status</dt>
            <dd className="text-amber-800">Wacht op bevestiging door de medewerker</dd>
            {resultaat.verificatie_verloopt_op && (
              <>
                <dt className="text-gray-500">Link geldig tot</dt>
                <dd className="text-gray-900">{new Date(resultaat.verificatie_verloopt_op).toLocaleString('nl-NL', { dateStyle: 'long', timeStyle: 'short' })}</dd>
              </>
            )}
          </dl>
          <p className="flex gap-2 text-sm text-gray-600">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
            De medewerker heeft een SMS gekregen. Pas na bevestiging komen de uren op de verzamelfactuur en telt het gesprek mee in het jaarverslag.
            Verloopt de link, dan kun je hem opnieuw versturen vanuit Ad-hoc uren.
          </p>
          {resultaat.deel5_toestemming === 'sms' && (
            <p className="text-sm text-gray-600">
              Op dezelfde SMS-pagina kan de medewerker kiezen of de management-update (Deel 5) met HR gedeeld mag worden. Zonder dat vinkje ziet HR niets.
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={() => { setResultaat(null); setG({ ...leegGesprek, employer_id: g.employer_id }); }}>Nog een melding</Button>
            <Link to="/professional/adhoc-uren"><Button variant="outline">Status volgen in Ad-hoc uren</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div>
        <Link to="/professional/agenda" className="inline-flex items-center text-sm text-blue-600 hover:underline mb-1"><ArrowLeft className="w-4 h-4 mr-1" />Agenda</Link>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900"><Footprints className="w-6 h-6" aria-hidden />Ad-hoc melding registreren</h1>
        <p className="text-sm text-gray-600 max-w-3xl">
          Voor een gesprek op de werkvloer zonder afspraak vooraf, bijvoorbeeld tijdens een zichtbaarheidsronde. De medewerker bevestigt het gesprek
          anoniem via een SMS-link die 72 uur geldig is. Tot die bevestiging tellen de uren en de registratie nergens mee.
        </p>
      </div>

      <section className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-4">
        <h2 className="font-semibold text-gray-900">Het gesprek</h2>
        <label className="block text-sm">
          <span className="text-gray-700">Opdrachtgever</span>
          <select className="mt-1 w-full rounded-md border-gray-300" value={g.employer_id} onChange={(e) => zet('employer_id', e.target.value)} required>
            <option value="">Kies…</option>
            {werkgevers.map((w) => <option key={w.employer_id} value={w.employer_id}>{w.organisatie}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-gray-700">Datum</span>
            <input type="date" className="mt-1 w-full rounded-md border-gray-300" value={g.datum} min={dagenTerug(60)} max={vandaag()} onChange={(e) => zet('datum', e.target.value)} required />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">Duur</span>
            <select className="mt-1 w-full rounded-md border-gray-300" value={g.tijd_minuten} onChange={(e) => zet('tijd_minuten', Number(e.target.value))}>
              {DUUR_OPTIES.map((m) => <option key={m} value={m}>{duurLabel(m)}</option>)}
            </select>
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-gray-700">Mobiel nummer van de medewerker (verplicht — anti-fraudegrendel)</span>
          <input type="tel" inputMode="tel" autoComplete="off" placeholder="06 12345678" className="mt-1 w-full rounded-md border-gray-300" value={g.employee_phone} onChange={(e) => zet('employee_phone', e.target.value)} required />
          <span className="mt-1 flex gap-1 text-xs text-gray-500">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0" aria-hidden />
            Alleen voor de SMS. We bewaren een onleesbare sleutel (HMAC) en de laatste drie cijfers; de werkgever ziet niets.
          </span>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">Toelichting voor de urenstaat (optioneel, zonder namen)</span>
          <input className="mt-1 w-full rounded-md border-gray-300" maxLength={500} value={g.toelichting} onChange={(e) => zet('toelichting', e.target.value)} />
        </label>
      </section>

      {error && <p className="text-sm text-red-700">{(error as any)?.response?.data?.message || 'Het verslag kon niet worden voorbereid.'}</p>}
      {ctx && (
        <GespreksverslagEngineForm
          key={`${g.employer_id}|${g.datum}`}
          context={ctx}
          adhoc={
            gesprekCompleet
              ? { employer_id: g.employer_id, datum: g.datum, tijd_minuten: g.tijd_minuten, employee_phone: g.employee_phone, toelichting: g.toelichting.trim() || undefined }
              : null
          }
          onSuccess={(r) => {
            setResultaat(r);
            qc.invalidateQueries({ queryKey: ['professional-adhoc-uren'] });
          }}
        />
      )}
    </div>
  );
};

export default ProfessionalAdhocMeldingPage;
